import type { Sight } from '../types';
import type { AssistantMessage, AssistantMarkdownMessage } from '../types/assistant';
import type { Locale } from '../../server/src/shared/locale';
import { $devConfig } from '../state/appConfig';
import { mapSight } from './poiClient';

const CONFIG_API_PATH = '/api/config';

export type AppClientConfig = {
  intro: AssistantMessage | AssistantMarkdownMessage;
  pois: Sight[];
};

type RawAppClientConfig = {
  intro: AssistantMessage | AssistantMarkdownMessage;
  pois: Sight[];
};

const normalizeApiHost = (value: string | undefined | null): string => {
  const trimmed = typeof value === 'string' ? value.trim() : '';
  return trimmed.replace(/\/+$/, '');
};

const getApiHost = (): string => {
  if (typeof window === 'undefined') {
    return '';
  }
  const { assistantApiHost } = $devConfig.getState();
  return normalizeApiHost(assistantApiHost);
};

const getAuthHeader = (): string | null => {
  if (typeof window === 'undefined') {
    return null;
  }
  const { assistantApiToken } = $devConfig.getState();
  const token = typeof assistantApiToken === 'string' ? assistantApiToken.trim() : '';
  if (!token) {
    return null;
  }
  return `Bearer ${token}`;
};

const buildHeaders = (): Record<string, string> => {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  const authHeader = getAuthHeader();
  if (authHeader) {
    headers.Authorization = authHeader;
  }
  return headers;
};

export const requestAppConfig = async (locale: Locale): Promise<AppClientConfig> => {
  const host = getApiHost();
  const url = `${host}${CONFIG_API_PATH}?locale=${encodeURIComponent(locale)}&responseFormat=markdown`;

  const response = await fetch(url, {
    method: 'GET',
    headers: buildHeaders(),
  });

  if (!response.ok) {
    let message = 'Failed to load app config.';
    try {
      const errorBody = (await response.json()) as { message?: string };
      if (typeof errorBody?.message === 'string') {
        message = errorBody.message;
      }
    } catch {
      // Ignore JSON parsing errors – fall back to generic text
    }
    throw new Error(message);
  }

  const data = (await response.json()) as RawAppClientConfig;

  return {
    intro: data.intro,
    pois: Array.isArray(data.pois) ? data.pois.map((sight) => mapSight(sight, locale)) : [],
  };
};
